import { CheckCircle2, XCircle } from 'lucide-react'
import clsx from 'clsx'

export interface ValidationJumpLink {
  key: string
  label: string
  anchorId: string
}

export interface ValidationBannerProps {
  valid: boolean
  jumpLinks: ValidationJumpLink[]
  onJump: (anchorId: string) => void
}

// Green "All changes valid" / red "N issues found" summary with one
// jump-link per failing {file_key, path} pair (PRD 07's own explicit
// content spec). Feature-local rather than folded into the shared `Banner`
// component - `Banner` supports at most one action button, not a list of
// links, so this reuses the same --color-success/--color-danger tokens and
// visual language directly instead of stretching Banner's own contract.
export function ValidationBanner({ valid, jumpLinks, onJump }: ValidationBannerProps) {
  const Icon = valid ? CheckCircle2 : XCircle

  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-control border px-4 py-3',
        valid ? 'border-success/40 bg-success/10 text-success' : 'border-danger/40 bg-danger/10 text-danger',
      )}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} strokeWidth={1.5} />
        <span className="text-body font-medium text-text-primary">
          {valid ? 'All changes valid' : `${jumpLinks.length} issue${jumpLinks.length === 1 ? '' : 's'} found`}
        </span>
      </div>
      {!valid && jumpLinks.length > 0 && (
        <ul className="flex flex-col gap-1 pl-8">
          {jumpLinks.map((link) => (
            <li key={link.key}>
              <button
                type="button"
                onClick={() => onJump(link.anchorId)}
                className="text-meta text-accent hover:underline"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
